import type { BattleSeat } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import type { ReactNode } from "react";

import { useProfile } from "../../lib/profile.js";
import { seatLabel } from "../../lib/seatLabel.js";

/** The seat's avatar: a lettered disc, tinted for the viewer's own seat, dashed while empty. */
export function SeatAvatar({
  seat,
  viewerEndUserId,
  size = 24,
}: {
  seat: BattleSeat;
  viewerEndUserId: string | null | undefined;
  size?: number;
}) {
  const label = seatLabel(seat, viewerEndUserId);
  return (
    <span
      aria-hidden
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold ${
        seat.kind === "empty"
          ? "border border-dashed border-border text-muted-foreground"
          : seat.kind === "bot"
            ? "bg-muted text-muted-foreground"
            : "bg-primary/15 text-primary"
      }`}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)) }}
    >
      {seat.kind === "empty" ? "?" : label === "You" ? "Y" : label.slice(0, 2).toUpperCase()}
    </span>
  );
}

/**
 * Wraps whatever names a seat. A player's seat becomes a button that opens their profile (boxed
 * opens its quick view from every seat, avatar and name); a bot's or an empty seat stays a plain
 * span, since there is no one to show. The button's accessible name is the seat's label, so an
 * avatar-only trigger (the live stage's compact header) still names the player.
 */
export function SeatProfileTrigger({
  seat,
  viewerEndUserId,
  className,
  children,
}: {
  seat: BattleSeat;
  viewerEndUserId: string | null | undefined;
  className?: string;
  children: ReactNode;
}) {
  const { showProfile } = useProfile();
  if (seat.kind !== "player" || !seat.endUser) {
    return <span className={className}>{children}</span>;
  }
  const endUserId = seat.endUser.id;
  const label = seatLabel(seat, viewerEndUserId);
  return (
    <button
      type="button"
      className={`cursor-pointer rounded-md text-left transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className ?? ""}`}
      aria-label={label === "You" ? "View your profile" : `View ${label}'s profile`}
      onClick={() => showProfile(endUserId)}
    >
      {children}
    </button>
  );
}

export function SeatName({
  seat,
  viewerEndUserId,
  className,
}: {
  seat: BattleSeat;
  viewerEndUserId: string | null | undefined;
  className?: string;
}) {
  const label = seatLabel(seat, viewerEndUserId);
  return (
    <span className={`inline-flex min-w-0 items-center gap-1.5 ${className ?? ""}`}>
      <SeatProfileTrigger
        seat={seat}
        viewerEndUserId={viewerEndUserId}
        className="inline-flex min-w-0 items-center gap-1.5"
      >
        <SeatAvatar seat={seat} viewerEndUserId={viewerEndUserId} />
        <span className="truncate text-sm font-semibold">{label}</span>
      </SeatProfileTrigger>
      {seat.isWinner ? (
        <Badge variant="outline" className="border-amber-500/50 text-amber-500">
          Winner
        </Badge>
      ) : null}
    </span>
  );
}
